import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, TextField, Button, Checkbox, FormControlLabel, Typography, Paper 
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import api from '../services/api';
import IntervalTable from '../components/IntervalTable';

export default function CreateUserPage() {
  const navigate = useNavigate();

  // Basic info
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);

  // Access settings
  const [accessAll, setAccessAll] = useState(false);
  const [intervals, setIntervals] = useState([]);
  const [bulkJson, setBulkJson] = useState(''); // If the admin wants to paste their own JSON

  const [statusMsg, setStatusMsg] = useState('');

  const buildAccessObject = () => {
    if (bulkJson.trim()) {
      // If the admin has pasted something in Bulk JSON, try to parse that
      try {
        return JSON.parse(bulkJson);
      } catch (err) {
        throw new Error('Invalid Bulk JSON');
      }
    } else {
      // Otherwise, build from "accessAll" plus intervals
      if (accessAll) {
        return { all: true };
      } else {
        const clientAccess = {};
        intervals.forEach((interval) => {
          const cid = interval.client_id;
          if (!clientAccess[cid]) {
            clientAccess[cid] = [];
          }
          clientAccess[cid].push({
            filename: interval.filename,
            start_date: interval.start_date,
            end_date: interval.end_date,
          });
        });
        return { all: false, client_access: clientAccess };
      }
    }
  };

  const handleCreateUser = async () => {
    try {
      const accessObject = buildAccessObject();
      const payload = {
        username,
        email,
        password,
        is_admin: isAdmin,
        access: accessObject,
      };
      await api.post('/admin/users', payload);
      setStatusMsg('User created successfully!');
      // Navigate back to dashboard or another page upon success
      navigate('/dashboard');
    } catch (error) {
      console.error(error);
      setStatusMsg(
        `Error creating user: ${error.response?.data?.detail || error.message}`
      );
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Paper elevation={3} sx={{ p: 3, maxWidth: 800, margin: 'auto' }}>
        <Typography variant="h4" gutterBottom>
          Create New User
        </Typography>

        {/* Basic user info */}
        <Box mb={2}>
          <TextField 
            label="Username" 
            value={username} 
            onChange={(e) => setUsername(e.target.value)} 
            fullWidth 
            sx={{ mb: 2 }}
          />
          <TextField 
            label="Email" 
            value={email} 
            onChange={(e) => setEmail(e.target.value)} 
            fullWidth
            sx={{ mb: 2 }}
          />
          <TextField 
            label="Password" 
            type="password"
            value={password} 
            onChange={(e) => setPassword(e.target.value)} 
            fullWidth
            sx={{ mb: 2 }}
          />
          <FormControlLabel
            control={
              <Checkbox 
                checked={isAdmin} 
                onChange={(e) => setIsAdmin(e.target.checked)} 
              />
            }
            label="Is Admin?"
          />
        </Box>

        {/* Access settings */}
        <Box mb={2}>
          <Typography variant="h6">Access Settings</Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={accessAll}
                onChange={(e) => setAccessAll(e.target.checked)}
              />
            }
            label="Access All?"
          />
        </Box>

        {!accessAll && (
          <Box mb={2}>
            <Typography variant="subtitle1" gutterBottom>
              Build intervals manually:
            </Typography>
            {/* The IntervalTable component allows adding multiple rows.
                Each row now contains fields for client ID, filename, and date pickers for start/end dates */}
            <IntervalTable intervals={intervals} setIntervals={setIntervals} />

            <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
              OR Paste Bulk JSON (this overrides the above intervals):
            </Typography>
            <TextField
              label="Bulk Access JSON"
              multiline
              minRows={4}
              value={bulkJson}
              onChange={(e) => setBulkJson(e.target.value)}
              fullWidth
            />
          </Box>
        )}

        <Box>
          <Button variant="contained" onClick={handleCreateUser}>
            Create User
          </Button>
        </Box>

        {statusMsg && (
          <Typography sx={{ mt: 2 }} color="primary">
            {statusMsg}
          </Typography>
        )}
      </Paper>
    </LocalizationProvider>
  );
}
